(() => {
  const RUNTIME_NAME = 'MarineMaxProductPageUtils';
  const VIEW_MORE_LABEL = 'View More';
  const VIEW_LESS_LABEL = 'View Less';
  const RESIZE_DELAY_MS = 100;

  const SELECTORS = Object.freeze({
    additionalHtmlData: 'script.additional-features-html-data',
    additionalHtmlMount: '.product-technical-specs__additional-html-mount',
    additionalHtmlOutput: '.product-technical-specs__additional-html-out',
    descriptionContent: '[data-description-content]',
    descriptionToggle: '[data-description-toggle]',
    featureContent: '[data-feature-list-content]',
    featureToggle: '[data-feature-list-toggle]',
    specsActiveIndicator: '.product-technical-specs__active-indicator',
    specsItem: '.product-technical-specs__accordion-item',
    specsOpenDetails: '.product-technical-specs__accordion-item details[open]',
    specsSection: '.product-technical-specs',
    specsText:
      '.product-technical-specs__spec-value, .product-technical-specs__spec-content.rte, .product-technical-specs__additional.rte',
  });

  const existingRuntime = window[RUNTIME_NAME];
  if (existingRuntime?.initialize) {
    existingRuntime.initialize();
    return;
  }

  const technicalSpecsControllers = new WeakMap();
  const descriptionControllers = new WeakMap();
  const featureListControllers = new WeakMap();
  let resizeTimeoutId = null;

  const collectElements = (root, selector) => {
    if (!root) return [];

    const elements = [];
    if (typeof root.matches === 'function' && root.matches(selector)) elements.push(root);
    if (typeof root.querySelectorAll === 'function') {
      elements.push(...root.querySelectorAll(selector));
    }

    return [...new Set(elements)];
  };

  const stripTrailingZeroDecimalTokens = (text) => {
    if (text == null || typeof text !== 'string') return text;
    return text.replace(/(\d+)\.(0+)(?=\D|$)/g, '$1');
  };

  const transformTextNodes = (root, transform) => {
    if (!root || typeof transform !== 'function') return;

    const ownerDocument = root.ownerDocument || document;
    const walker = ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let textNode = walker.nextNode();

    while (textNode) {
      textNodes.push(textNode);
      textNode = walker.nextNode();
    }

    textNodes.forEach((node) => {
      const transformedValue = transform(node.nodeValue);
      if (transformedValue !== node.nodeValue) node.nodeValue = transformedValue;
    });
  };

  const stripTrailingZeroDecimalsInTechnicalSpecs = (specsSection) => {
    specsSection.querySelectorAll(SELECTORS.specsText).forEach((element) => {
      transformTextNodes(element, stripTrailingZeroDecimalTokens);
    });
  };

  const hydrateTechnicalSpecsAdditionalHtml = (specsSection) => {
    specsSection.querySelectorAll(SELECTORS.additionalHtmlMount).forEach((mount) => {
      const dataElement = mount.querySelector(SELECTORS.additionalHtmlData);
      const output = mount.querySelector(SELECTORS.additionalHtmlOutput);
      if (!dataElement || !output || output.dataset.parsed === '1') return;

      const rawHtmlPayload = dataElement.textContent.trim();
      if (!rawHtmlPayload) return;

      try {
        const parsedHtml = JSON.parse(rawHtmlPayload);
        if (typeof parsedHtml === 'string') {
          output.innerHTML = parsedHtml;
          output.dataset.parsed = '1';
        }
      } catch (error) {
        console.warn('Technical specs additional features: could not parse HTML payload', error);
      }

      dataElement.remove();
    });

    stripTrailingZeroDecimalsInTechnicalSpecs(specsSection);
  };

  const collapseAndPreserveViewport = (content, anchor, collapse) => {
    const focusAnchor = anchor || content;
    const beforeTop = focusAnchor.getBoundingClientRect().top;
    const inlineTransition = content.style.transition;

    content.style.transition = 'none';
    collapse();

    void content.offsetHeight;

    requestAnimationFrame(() => {
      if (!content.isConnected) return;

      const afterTop = focusAnchor.getBoundingClientRect().top;
      const scrollDelta = afterTop - beforeTop;
      if (Math.abs(scrollDelta) > 1) window.scrollBy(0, scrollDelta);

      requestAnimationFrame(() => {
        if (!content.isConnected) return;

        if (inlineTransition) {
          content.style.transition = inlineTransition;
        } else {
          content.style.removeProperty('transition');
        }
      });
    });
  };

  class TechnicalSpecsController {
    constructor(section) {
      this.section = section;
      this.items = Array.from(section.querySelectorAll(SELECTORS.specsItem));
      this.indicator = section.querySelector(SELECTORS.specsActiveIndicator);
      this.details = this.items
        .map((item) => item.querySelector('details'))
        .filter(Boolean);
      this.handleToggle = this.updateIndicator.bind(this);

      hydrateTechnicalSpecsAdditionalHtml(section);
      this.details.forEach((details) => details.addEventListener('toggle', this.handleToggle));
      this.updateIndicator();
    }

    updateIndicator() {
      if (!this.indicator || !this.section.isConnected) return;

      const openDetails = this.section.querySelector(SELECTORS.specsOpenDetails);
      this.items.forEach((item) => item.classList.remove('active'));

      const activeItem = openDetails?.closest(SELECTORS.specsItem);
      if (!activeItem) {
        this.indicator.style.opacity = '0';
        return;
      }

      activeItem.classList.add('active');
      this.indicator.style.top = `${activeItem.offsetTop}px`;
      this.indicator.style.height = `${activeItem.offsetHeight}px`;
      this.indicator.style.opacity = '1';
    }

    destroy() {
      this.details.forEach((details) => details.removeEventListener('toggle', this.handleToggle));
    }
  }

  class DescriptionController {
    constructor(content, toggleButton) {
      this.content = content;
      this.toggleButton = toggleButton;
      this.isExpanded = false;
      this.measureFrameId = null;
      this.handleToggle = this.toggle.bind(this);

      this.content.setAttribute('data-description-collapsed', 'true');
      this.content.setAttribute('data-description-expanded', 'false');
      this.toggleButton.textContent = VIEW_MORE_LABEL;
      this.measureFrameId = requestAnimationFrame(() => this.measure());
    }

    measure() {
      this.measureFrameId = null;
      if (!this.content.isConnected) return;

      const computedStyle = getComputedStyle(this.content);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const fontSize = parseFloat(computedStyle.fontSize);
      const actualLineHeight = lineHeight || fontSize * 1.6;
      const collapsedHeight = actualLineHeight * 4;
      const originalDisplay = this.content.style.display;
      const originalLineClamp = this.content.style.webkitLineClamp;

      this.content.style.display = 'block';
      this.content.style.webkitLineClamp = 'none';
      const fullHeight = this.content.scrollHeight;
      this.content.style.display = originalDisplay;
      this.content.style.webkitLineClamp = originalLineClamp;

      if (fullHeight > collapsedHeight) {
        this.toggleButton.style.display = 'block';
        this.toggleButton.addEventListener('click', this.handleToggle);
        return;
      }

      this.toggleButton.style.display = 'none';
      this.content.removeAttribute('data-description-collapsed');
      this.content.removeAttribute('data-description-expanded');
    }

    toggle() {
      if (this.isExpanded) {
        collapseAndPreserveViewport(this.content, this.toggleButton, () => {
          this.isExpanded = false;
          this.content.setAttribute('data-description-collapsed', 'true');
          this.content.setAttribute('data-description-expanded', 'false');
          this.toggleButton.textContent = VIEW_MORE_LABEL;
        });
        return;
      }

      this.isExpanded = true;
      this.content.setAttribute('data-description-expanded', 'true');
      this.content.setAttribute('data-description-collapsed', 'false');
      this.toggleButton.textContent = VIEW_LESS_LABEL;
    }

    destroy() {
      if (this.measureFrameId !== null) cancelAnimationFrame(this.measureFrameId);
      this.toggleButton.removeEventListener('click', this.handleToggle);
    }
  }

  class FeatureListController {
    constructor(content, toggleButton) {
      this.content = content;
      this.toggleButton = toggleButton;
      this.isExpanded = false;
      this.measureFrameId = null;
      this.handleToggle = this.toggle.bind(this);

      const desktopMax = parseFloat(content.getAttribute('data-feature-list-max-height')) || 420;
      const mobileMax = parseFloat(content.getAttribute('data-feature-list-max-height-mobile')) || 360;
      this.collapsedHeight = window.matchMedia('(max-width: 767px)').matches
        ? mobileMax
        : desktopMax;

      this.content.style.setProperty('--feature-list-max-height', `${desktopMax}px`);
      this.content.style.setProperty('--feature-list-max-height-mobile', `${mobileMax}px`);
      this.content.setAttribute('data-feature-list-collapsed', 'true');
      this.content.setAttribute('data-feature-list-expanded', 'false');
      this.toggleButton.textContent = VIEW_MORE_LABEL;
      this.measureFrameId = requestAnimationFrame(() => this.measure());
    }

    measure() {
      this.measureFrameId = null;
      if (!this.content.isConnected) return;

      if (this.content.scrollHeight > this.collapsedHeight) {
        this.toggleButton.style.display = 'block';
        this.toggleButton.addEventListener('click', this.handleToggle);
        return;
      }

      this.toggleButton.style.display = 'none';
      this.content.removeAttribute('data-feature-list-collapsed');
      this.content.removeAttribute('data-feature-list-expanded');
    }

    toggle() {
      if (this.isExpanded) {
        collapseAndPreserveViewport(this.content, this.toggleButton, () => {
          this.isExpanded = false;
          this.content.setAttribute('data-feature-list-collapsed', 'true');
          this.content.setAttribute('data-feature-list-expanded', 'false');
          this.toggleButton.textContent = VIEW_MORE_LABEL;
        });
        return;
      }

      this.isExpanded = true;
      this.content.setAttribute('data-feature-list-expanded', 'true');
      this.content.setAttribute('data-feature-list-collapsed', 'false');
      this.toggleButton.textContent = VIEW_LESS_LABEL;
    }

    destroy() {
      if (this.measureFrameId !== null) cancelAnimationFrame(this.measureFrameId);
      this.toggleButton.removeEventListener('click', this.handleToggle);
    }
  }

  const initializeTechnicalSpecs = (root = document) => {
    let initializedCount = 0;

    collectElements(root, SELECTORS.specsSection).forEach((section) => {
      if (technicalSpecsControllers.has(section)) return;
      technicalSpecsControllers.set(section, new TechnicalSpecsController(section));
      initializedCount += 1;
    });

    return initializedCount;
  };

  const initializeDescriptionViewMore = (root = document) => {
    let initializedCount = 0;

    collectElements(root, SELECTORS.descriptionContent).forEach((content) => {
      if (descriptionControllers.has(content)) return;

      const toggleButton = content.parentElement?.querySelector(SELECTORS.descriptionToggle);
      if (!toggleButton) return;

      descriptionControllers.set(content, new DescriptionController(content, toggleButton));
      initializedCount += 1;
    });

    return initializedCount;
  };

  const initializeFeatureListViewMore = (root = document) => {
    let initializedCount = 0;

    collectElements(root, SELECTORS.featureContent).forEach((content) => {
      if (featureListControllers.has(content)) return;

      const toggleButton = content.parentElement?.querySelector(SELECTORS.featureToggle);
      if (!toggleButton) return;

      featureListControllers.set(content, new FeatureListController(content, toggleButton));
      initializedCount += 1;
    });

    return initializedCount;
  };

  const initialize = (root = document) => {
    const technicalSpecs = initializeTechnicalSpecs(root);
    const descriptions = initializeDescriptionViewMore(root);
    const featureLists = initializeFeatureListViewMore(root);

    return { descriptions, featureLists, technicalSpecs };
  };

  const destroyControllers = (root, selector, controllerMap) => {
    collectElements(root, selector).forEach((element) => {
      const controller = controllerMap.get(element);
      if (!controller) return;
      controller.destroy();
      controllerMap.delete(element);
    });
  };

  const destroy = (root) => {
    if (!root) return;
    destroyControllers(root, SELECTORS.specsSection, technicalSpecsControllers);
    destroyControllers(root, SELECTORS.descriptionContent, descriptionControllers);
    destroyControllers(root, SELECTORS.featureContent, featureListControllers);
  };

  const handleResize = () => {
    clearTimeout(resizeTimeoutId);
    resizeTimeoutId = setTimeout(() => {
      resizeTimeoutId = null;
      collectElements(document, SELECTORS.specsSection).forEach((section) => {
        technicalSpecsControllers.get(section)?.updateIndicator();
      });
    }, RESIZE_DELAY_MS);
  };

  const runtime = {
    destroy,
    initialize,
    initializeDescriptionViewMore,
    initializeFeatureListViewMore,
    initializeTechnicalSpecs,
  };

  window[RUNTIME_NAME] = runtime;

  // Backward-compatible entrypoints retained for any app code outside this theme archive.
  window.initTechnicalSpecs = initializeTechnicalSpecs;
  window.initDescriptionViewMore = initializeDescriptionViewMore;
  window.initFeatureListViewMore = initializeFeatureListViewMore;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  window.addEventListener('resize', handleResize);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
